import React, { useState, useEffect } from 'react';
import styled from 'styled-components';

const HeaderWrapper = styled.header`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  z-index: 1000;
  background: ${({ $scrolled }) => 
    $scrolled ? 'rgba(255, 255, 255, 0.98)' : 'rgba(255, 255, 255, 0.95)'};
  backdrop-filter: blur(20px);
  border-bottom: 1px solid ${({ $scrolled }) => 
    $scrolled ? 'rgba(0, 102, 204, 0.15)' : 'rgba(0, 102, 204, 0.1)'};
  transition: all 0.3s ease;
  box-shadow: ${({ $scrolled }) => 
    $scrolled ? '0 4px 20px rgba(0, 102, 204, 0.15)' : '0 2px 10px rgba(0, 102, 204, 0.08)'};
`;

const Container = styled.div`
  max-width: 1400px;
  margin: 0 auto;
  //padding: ${({ theme }) => theme.spacing.md} ${({ theme }) => theme.spacing.lg};
  display: flex;
  align-items: center;
  justify-content: space-between;
  
  // @media (max-width: ${({ theme }) => theme.breakpoints.tablet}) {
  //   padding: ${({ theme }) => theme.spacing.sm} ${({ theme }) => theme.spacing.md};
  // }
`;

const LogoContainer = styled.div`
  display: flex;
  align-items: center;
  cursor: pointer;
  transition: transform 0.3s ease;
  
  &:hover {
    transform: scale(1.05);
  }
`;

const LogoImage = styled.img`
  height: 125px;
  width: auto;
  display: block;
  
  @media (max-width: ${({ theme }) => theme.breakpoints.mobile}) {
    height: 55px;
  }
`;

const Nav = styled.nav`
  display: flex;
  align-items: center;
  gap: ${({ theme }) => theme.spacing.xl};
  
  @media (max-width: ${({ theme }) => theme.breakpoints.desktop}) {
    gap: ${({ theme }) => theme.spacing.lg};
  }
  
  @media (max-width: ${({ theme }) => theme.breakpoints.tablet}) {
    position: fixed;
    top: 70px;
    left: 0;
    right: 0;
    background: rgba(255, 255, 255, 0.98);
    backdrop-filter: blur(20px);
    flex-direction: column;
    padding: ${({ theme }) => theme.spacing.xl};
    gap: ${({ theme }) => theme.spacing.md};
    box-shadow: 0 10px 30px rgba(0, 0, 0, 0.1);
    border-bottom: 1px solid rgba(0, 0, 0, 0.1);
    transform: ${({ $isOpen }) => $isOpen ? 'translateY(0)' : 'translateY(-100%)'};
    opacity: ${({ $isOpen }) => $isOpen ? '1' : '0'};
    visibility: ${({ $isOpen }) => $isOpen ? 'visible' : 'hidden'};
    transition: all 0.3s ease;
  }
`;

const NavLink = styled.a`
  color: #1e293b;
  font-size: ${({ theme }) => theme.fontSizes.base};
  font-weight: ${({ theme }) => theme.fontWeights.medium};
  text-decoration: none;
  position: relative;
  padding: ${({ theme }) => theme.spacing.xs} 0;
  transition: all 0.3s ease;
  cursor: pointer;
  
  &::after {
    content: '';
    position: absolute;
    bottom: 0;
    left: 0;
    width: 0;
    height: 2px;
    background: ${({ theme }) => theme.colors.primary};
    transition: width 0.3s ease;
  }
  
  &:hover {
    color: ${({ theme }) => theme.colors.primary};
    
    &::after {
      width: 100%;
    }
  }
  
  @media (max-width: ${({ theme }) => theme.breakpoints.tablet}) {
    color: #1e293b;
    font-size: ${({ theme }) => theme.fontSizes.lg};
    width: 100%;
    padding: ${({ theme }) => theme.spacing.sm} 0;
    
    &::after {
      background: ${({ theme }) => theme.colors.primary};
    }
  }
`;

const CTAButton = styled.button`
  background: linear-gradient(135deg, ${({ theme }) => theme.colors.primary} 0%, ${({ theme }) => theme.colors.secondary} 100%);
  color: #ffffff;
  font-size: ${({ theme }) => theme.fontSizes.base};
  font-weight: ${({ theme }) => theme.fontWeights.semibold};
  padding: ${({ theme }) => theme.spacing.sm} ${({ theme }) => theme.spacing.lg};
  border-radius: ${({ theme }) => theme.borderRadius.full};
  border: 2px solid transparent;
  transition: all 0.3s ease;
  cursor: pointer;
  box-shadow: 0 4px 15px rgba(0, 102, 204, 0.3);
  
  &:hover {
    transform: translateY(-2px) scale(1.05);
    box-shadow: 0 8px 25px rgba(0, 102, 204, 0.5);
  }
  
  @media (max-width: ${({ theme }) => theme.breakpoints.tablet}) {
    width: 100%;
    padding: ${({ theme }) => theme.spacing.md} ${({ theme }) => theme.spacing.xl};
    font-size: ${({ theme }) => theme.fontSizes.lg};
  }
`;

const MobileMenuButton = styled.button`
  display: none;
  background: rgba(0, 102, 204, 0.1);
  border: 2px solid rgba(0, 102, 204, 0.3);
  border-radius: ${({ theme }) => theme.borderRadius.md};
  padding: ${({ theme }) => theme.spacing.sm};
  cursor: pointer;
  transition: all 0.3s ease;
  
  &:hover {
    background: rgba(0, 102, 204, 0.2);
  }
  
  @media (max-width: ${({ theme }) => theme.breakpoints.tablet}) {
    display: flex;
    flex-direction: column;
    gap: 4px;
  }
`;

const MenuLine = styled.span`
  width: 24px;
  height: 2px;
  background: ${({ theme }) => theme.colors.primary};
  border-radius: 2px;
  transition: all 0.3s ease;
  
  ${MobileMenuButton}:hover & {
    background: ${({ theme }) => theme.colors.secondary};
  }
  
  ${({ $isOpen }) => $isOpen && `
    &:nth-child(1) {
      transform: rotate(45deg) translateY(9px);
    }
    &:nth-child(2) {
      opacity: 0;
    }
    &:nth-child(3) {
      transform: rotate(-45deg) translateY(-9px);
    }
  `}
`;

export const Header = () => {
  const [scrolled, setScrolled] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  useEffect(() => {
    const handleScroll = () => {
      setScrolled(window.scrollY > 50);
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const scrollToSection = (sectionId) => {
    const element = document.getElementById(sectionId);
    if (element) {
      const offset = 80; // Header height
      const elementPosition = element.getBoundingClientRect().top;
      const offsetPosition = elementPosition + window.pageYOffset - offset;

      window.scrollTo({
        top: offsetPosition,
        behavior: 'smooth'
      });
      setMobileMenuOpen(false);
    }
  };

  const scrollToTop = () => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
    setMobileMenuOpen(false);
  };

  return (
    <HeaderWrapper $scrolled={scrolled}>
      <Container>
        <LogoContainer onClick={scrollToTop}>
          <LogoImage src="/assets/logo.jpeg" alt="Logg Healthcare" />
        </LogoContainer>

        <MobileMenuButton 
          $scrolled={scrolled}
          onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
          aria-label="Toggle menu"
        >
          <MenuLine $scrolled={scrolled} $isOpen={mobileMenuOpen} />
          <MenuLine $scrolled={scrolled} $isOpen={mobileMenuOpen} />
          <MenuLine $scrolled={scrolled} $isOpen={mobileMenuOpen} />
        </MobileMenuButton>

        <Nav $isOpen={mobileMenuOpen}>
          <NavLink 
            $scrolled={scrolled} 
            onClick={() => scrollToTop()}
          >
            Home
          </NavLink>
          <NavLink 
            $scrolled={scrolled} 
            onClick={() => scrollToSection('services')}
          >
            Services
          </NavLink>
          <NavLink 
            $scrolled={scrolled} 
            onClick={() => scrollToSection('about')}
          >
            About Us
          </NavLink>
          <NavLink 
            $scrolled={scrolled} 
            onClick={() => scrollToSection('features')}
          >
            Why Choose Us
          </NavLink>
          <NavLink 
            $scrolled={scrolled} 
            onClick={() => scrollToSection('testimonials')}
          >
            Testimonials
          </NavLink>
          <NavLink 
            $scrolled={scrolled} 
            onClick={() => scrollToSection('contact')}
          >
            Contact
          </NavLink>
          <CTAButton 
            $scrolled={scrolled}
            onClick={() => scrollToSection('appointment')}
          >
            Get Started
          </CTAButton>
        </Nav>
      </Container>
    </HeaderWrapper>
  );
};
